// src/realtimeDogs.js
import { db, auth } from "./firebase";
import { doc, getDoc, onSnapshot, setDoc, serverTimestamp, runTransaction, updateDoc } from "firebase/firestore";
import { debounce } from 'lodash-es';
import { makeDayKey, parseDayKey, assertDayKey } from "@/lib/dateUtils";
import { ENABLE_AUTO_SAVE_DOGS } from "./config/featureFlags";
import { getDogIdentity } from "@/lib/dogIdentity";

// Función de validación de UID
function ensureUid(uid){
  return typeof uid === 'string' && uid.length > 0;
}

/**
 * Sanitiza datos para Firestore eliminando undefined y valores inválidos
 * que pueden causar POST 400 errors
 */
// Fase 2 integridad: exportada para que dogDayManagerActions.js pueda
// sanitizar escrituras que ya no pasan por saveDogs() (las que ahora usan
// runTransaction directamente para evitar carreras con reorder/edición).
export function sanitizeForFirestore(value) {
  // Elimina undefined y convierte NaN/Infinity a null
  if (value === undefined) return undefined; // señal para quitar la clave
  if (typeof value === "number" && !Number.isFinite(value)) return null;
  if (value && typeof value === "object") {
    if (Array.isArray(value)) {
      const arr = value.map(sanitizeForFirestore).filter(v => v !== undefined);
      return arr;
    }
    const out = {};
    for (const k of Object.keys(value)) {
      const v = sanitizeForFirestore(value[k]);
      if (v !== undefined) out[k] = v; // no copiar campos undefined
    }
    return out;
  }
  return value; // strings, boolean, numbers finitos, null
}

/** Normaliza una lista (strings u objetos) a array de objetos de perros completos */
function toDogObjectArray(list) {
  const items = Array.isArray(list) ? list : [];
  const dogs = [];
  const seen = new Set();
  
  for (const item of items) {
    let dogObj;
    
    // Si es string (formato antiguo), convertir a objeto
    if (typeof item === "string") {
      const name = item.trim();
      if (!name) continue;
      dogObj = {
        name: name,
        originalOrder: dogs.length,
        count: 1,
        isFixed: true,
        whatsappGroup: null,
        firstAddedDateKey: null,
        informativeNote: ""
      };
    } 
    // Si es objeto, normalizar
    else if (item && typeof item === "object") {
      const name = String(item.name || "").trim();
      if (!name) continue;
      dogObj = {
        name: name,
        // Identidad estable: preservar id/importKey si existen (creados en alta manual
        // o importación PDF). Sin esto, cada round-trip por Firestore los perdería,
        // rompiendo la identidad estable frente a renombrados.
        ...(item.id ? { id: String(item.id) } : {}),
        ...(item.importKey ? { importKey: String(item.importKey) } : {}),
        originalOrder: typeof item.originalOrder === "number" ? item.originalOrder : dogs.length,

        count: typeof item.count === "number" && item.count > 0 ? item.count : 1,
        isFixed: typeof item.isFixed === "boolean" ? item.isFixed : true,
        whatsappGroup: item.whatsappGroup || null,
        locationUrl: item.locationUrl || null,
        firstAddedDateKey: item.firstAddedDateKey || null,
        informativeNote: item.informativeNote || "",
        // Horarios opcionales: backward compatible (clientes antiguos sin schedules → [])
        schedules: Array.isArray(item.schedules)
          ? item.schedules
              .filter(s => s && typeof s === 'object' && Array.isArray(s.days) && s.days.length > 0)
              .map(s => ({
                id: String(s.id || ''),
                arrivalTime: typeof s.arrivalTime === 'string' ? s.arrivalTime : '',
                departureTime: typeof s.departureTime === 'string' ? s.departureTime : '',
                days: s.days.filter(d => ['mon','tue','wed','thu','fri','sat','sun'].includes(d)),
                // Preservar campos "solo esta semana". Clientes antiguos sin el campo → false (recurrente)
                onlyThisWeek: typeof s.onlyThisWeek === 'boolean' ? s.onlyThisWeek : false,
                ...(s.onlyThisWeek && s.weekStartDateKey ? { weekStartDateKey: String(s.weekStartDateKey) } : {}),
              }))
              .filter(s => s.days.length > 0)
          : [],
        recurringNotes: Array.isArray(item.recurringNotes) ? item.recurringNotes : [],
        informativeNoteOverride: typeof item.informativeNoteOverride === "boolean"
          ? item.informativeNoteOverride
          : false,
      };
    } else {
      continue;
    }
    
    // Fase 2: evitar duplicados por IDENTIDAD ESTABLE (id si existe, con
    // fallback legacy al nombre normalizado). Antes se deduplicaba solo por
    // dogObj.name, lo que podía fusionar dos clientes distintos con el mismo
    // nombre visible, o fallar en detectar duplicados de un mismo cliente
    // renombrado en dos entradas.
    const identity = getDogIdentity(dogObj);
    if (!seen.has(identity)) {
      seen.add(identity);
      dogs.push(dogObj);
    }
  }
  
  // Ordenar por originalOrder
  dogs.sort((a, b) => a.originalOrder - b.originalOrder);
  
  return dogs;
}

/**
 * Lectura puntual (bajo demanda, sin suscripción) de un día/perfil.
 * Usada por EditDogForm para navegar a semanas futuras no cargadas en
 * memoria sin crear ningún documento ni suscribirse en tiempo real.
 * - Nunca escribe.
 * - Si el documento no existe, devuelve dogs=[] y urgentDogs={}.
 */
export async function fetchDogDocForDay(workspaceId, dayKey, profile) {
  if (!ensureUid(workspaceId) || !profile) {
    return { dogs: [], urgentDogs: {} };
  }
  try {
    assertDayKey(dayKey);
  } catch (error) {
    console.error('[DAYKEY_ERROR]', error.message);
    return { dogs: [], urgentDogs: {} };
  }

  const ref = doc(db, "workspaces", workspaceId, "days", dayKey, "profiles", profile);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    return { dogs: [], urgentDogs: {} };
  }
  const data = snap.data() || {};
  return {
    dogs: toDogObjectArray(data.dogs || []),
    urgentDogs: data.urgentDogs || {},
  };
}

/** Suscripción: devolvemos array de objetos de perros completos */
export function subscribeDogs(workspaceId, dayKey, callback, options = {}) {

  const { visibleProfile = '', includeMetadataChanges = true } = options;
  if (!ensureUid(workspaceId)) {
    return () => {};
  }

  if (!visibleProfile) {
    console.warn('[SUB_DOGS] Missing visibleProfile');
    return () => {};
  }

  try {
    assertDayKey(dayKey);
  } catch (error) {
    console.error('[DAYKEY_ERROR]', error.message);
    return () => {};
  }

   const ref = doc(db, "workspaces", workspaceId, "days", dayKey, "profiles", visibleProfile);

   console.log('[DOGS_SUBSCRIBE_PATH]', {
     workspaceId,
     dayKey,
     visibleProfile,
     visibleProfileJson: JSON.stringify(visibleProfile),
     path: `workspaces/${workspaceId}/days/${dayKey}/profiles/${visibleProfile}`,
   });

   const unsubscribe = onSnapshot(
    ref,
    { includeMetadataChanges },
    (snap) => {
       console.log('[DOGS_SNAPSHOT_RAW]', {
         workspaceId,
         dayKey,
         visibleProfile,
         exists: snap.exists(),
         fromCache: snap.metadata?.fromCache,
         hasPendingWrites: snap.metadata?.hasPendingWrites,
         dogs: Array.isArray(snap.data()?.dogs)
           ? snap.data().dogs.map(dog => ({
               id: dog?.id,
               importKey: dog?.importKey,
               name: dog?.name,
             }))
           : snap.data()?.dogs,
       });
       const data = snap.data?.() || null;
       const dogs = toDogObjectArray(data?.dogs || []);
       // Metadata mínima del snapshot: permite al consumidor (AppCore)
       // distinguir un snapshot remoto CONFIRMADO por el servidor
       // (fromCache=false, hasPendingWrites=false) de uno puramente
       // cacheado/transitorio, sin cambiar la firma obligatoria del
       // callback (segundo argumento opcional, ignorable por callers
       // existentes).
       const metadata = {
         fromCache: snap.metadata?.fromCache ?? false,
         hasPendingWrites: snap.metadata?.hasPendingWrites ?? false,
       };
       callback(dogs, metadata);
    },

    (err) => {
      // LOG: [SUB_DOGS_SNAPSHOT_ERR] - Error in snapshot subscription
      console.error('[SUB_DOGS_SNAPSHOT_ERR]', JSON.stringify({
        profile: visibleProfile,
        dayKey,
        refPath: ref.path,
        code: err.code || err.name,
        message: err.message
      }));
    }
  );
  
  return unsubscribe;
}

/**
 * Guardado completo: guarda la lista de objetos de perros con toda su información.
 * El servidor es la fuente de verdad. La sincronización bidireccional
 * en AppCore.jsx se encarga de resolver conflictos.
 * 
 * ✅ ACTUALIZADO:
 * - Acepta dogs=[] (persiste arrays vacíos para merge correcto)
 * - Valida dayKey NO sea composite (no contiene "::")
 * - SIEMPRE usa setDoc con merge: true
 * - Nunca llama deleteDoc()
 * - Añade logs [DAYS_WRITE]
 */
export async function saveDogs(workspaceId, dayKey, dogList = [], profileName) {
  // 🔒 FEATURE FLAG: Desactivar temporalmente auto-guardado de perros para limpiar la BD
  if (!ENABLE_AUTO_SAVE_DOGS) {
    console.log("[SAVE_DOGS_FN] Bloqueado por ENABLE_AUTO_SAVE_DOGS=false", {
      workspaceId,
      dayKey,
      profileName,
      dogsCount: Array.isArray(dogList) ? dogList.length : "n/a",
    });
    return;
  }

  if (!ensureUid(workspaceId)) { 
    console.warn('[AUTH_NULL]', { fn:'saveDogs' }); 
    return; 
  }

  if (!profileName) {
    console.warn('[SAVE_DOGS] Missing profileName, skipping save');
    return;
  }

  try {
    assertDayKey(dayKey);
  } catch (error) {
    console.error('[DAYKEY_ERROR]', error.message);
    return;
  }

  // ✅ NUEVO: Validar que dayKey NO sea composite (contiene "::")
  let normalizedDayKey = dayKey;
  if (dayKey.includes('::')) {
    console.error('[DAYKEY_COMPOSITE_ERROR]', JSON.stringify({
      original: dayKey,
      message: 'dayKey contiene "::" - debe ser YYYY-MM-DD, corrigiendo',
      corrected: dayKey.split('::')[1]
    }));
    normalizedDayKey = dayKey.split('::')[1];
  }

  // ✅ PERMITE ARRAYS VACÍOS: toDogObjectArray([]) → []
  // NO hacer early return por vacío - permitir persistir []
  const dogsRaw = toDogObjectArray(dogList); // Puede ser []
  const dogs = sanitizeForFirestore(dogsRaw);
  
  // ✅ CORRECTO: Path sin errores (era 'workspaceId' antes)
  const ref = doc(db, 'workspaces', workspaceId, 'days', normalizedDayKey, 'profiles', profileName);
  
  // ✅ NUEVO: Log [DAYS_WRITE]
  console.error("[DAYS_WRITE]", JSON.stringify({
    workspaceId,
    profile: profileName,
    dayKey: normalizedDayKey,
    dogsLen: Array.isArray(dogs) ? dogs.length : 0,
    order: Array.isArray(dogs) ? dogs.map(dog => ({
      identity: getDogIdentity(dog),
      originalOrder: dog.originalOrder
    })) : [],
    path: ref.path,
    timestamp: new Date().toISOString()
  }));
  
  try {
    // ✅ SIEMPRE: setDoc con merge: true (incluso si dogs=[])
    await setDoc(ref, { 
      dogs, 
      count: dogs.length, 
      _updatedAt: serverTimestamp()
    }, { merge: true });
    
    console.log(`[SAVE_DOGS] ✓ saved ${dogs.length} dogs for ${normalizedDayKey} (profile: ${profileName})`);
  } catch (e) {
    console.error("[SAVE_DOGS_ERR]", ref.path, e.code || e.name);
    throw e;
  }
}

/**
 * Guardado de estados (completedDogs y urgentDogs)
 * Los estados se guardan por perfil para evitar conflictos entre ventanas
 */
/**
 * Función de reintento con backoff exponencial
 */
async function withRetry(fn, maxRetries = 3, baseDelay = 1000) {
  let lastError = null;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Ignorar AbortError y errores de red
      if (error.name === 'AbortError') {
        console.warn(`[RETRY] Operation aborted on attempt ${attempt + 1}`);
        return null;
      }
      
      // Log del error
      console.warn(`[RETRY] Attempt ${attempt + 1} failed:`, error.code || error.name);
      
      // Backoff exponencial
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  // Si todos los intentos fallan
  console.error('[RETRY] All attempts failed', lastError);
  throw lastError;
}

export async function saveStates(workspaceId, dayKey, completedDogs = {}, urgentDogs = {}, profileName) {
  if (!ensureUid(workspaceId)) { 
    console.warn('[AUTH_NULL]', { fn:'saveStates' }); 
    return; 
  }

  if (!profileName) {
    console.warn('[SAVE_STATES] Missing profileName, skipping save');
    return;
  }

  try {
    assertDayKey(dayKey);
    console.info('[DAYKEY_CHECK]', { dayKey, parsed: parseDayKey(dayKey) });
  } catch (error) {
    console.error('[DAYKEY_ERROR]', error.message);
    return;
  }

  const ref = doc(db, 'workspaces', workspaceId, 'days', dayKey, 'profiles', profileName);
  
  // Validación inicial
  if (!workspaceId || !dayKey) {
    console.warn('[SAVE_STATES] Invalid input, skipping save');
    return;
  }
  
  // Sanitizar estados
  const sanitizedCompleted = sanitizeForFirestore(completedDogs || {});
  const sanitizedUrgent = sanitizeForFirestore(urgentDogs || {});
  
  // Reducir logs ruidosos
  if (Object.keys(sanitizedCompleted).length > 0 || Object.keys(sanitizedUrgent).length > 0) {
    console.log("[SAVE_STATES] Saving states", {
      path: ref.path,
      completedCount: Object.keys(sanitizedCompleted).length,
      urgentCount: Object.keys(sanitizedUrgent).length
    });
  }

  return withRetry(async () => {
    try {
      const dataToSave = {
        completedDogs: sanitizedCompleted,
        urgentDogs: sanitizedUrgent,
        _statesUpdatedAt: serverTimestamp()
      };

      await setDoc(ref, dataToSave, { merge: true });
      console.log("[SAVE_STATES] ✓ saved states for profile:", profileName);
    } catch (error) {
      console.error("[SAVE_STATES] FAIL", ref.path, error.code || error.name, error.message);
      throw error;
    }
  });
}

// Debounce para saveStates
export const debouncedSaveStates = debounce(saveStates, 500, { 
  leading: false,  // No ejecutar inmediatamente
  trailing: true   // Ejecutar después del tiempo de espera
});

/**
 * Suscripción a cambios de estados (completedDogs y urgentDogs)
 * Se suscribe al documento del perfil específico
 */
export function subscribeStates(workspaceId, dayKey, callback, options = {}) {
  const { includeMetadataChanges = true, visibleProfile = '' } = options;
  if (!ensureUid(workspaceId)) { 
    console.warn('[AUTH_NULL]', { fn:'subscribeStates' }); 
    return () => {}; 
  }

  if (!visibleProfile) {
    console.warn('[SUB_STATES] Missing visibleProfile, skipping subscription');
    return () => {};
  }

  const ref = doc(db, "workspaces", workspaceId, "days", dayKey, "profiles", visibleProfile);
  console.log("[SUB_STATES_START] creating subscription", { workspaceId, dayKey, profile: visibleProfile, path: ref.path });

  return onSnapshot(ref, { includeMetadataChanges }, (snap) => {
    console.log("[SUB_STATES_SNAPSHOT] received snapshot", {
      workspaceId,
      dayKey,
      profile: visibleProfile,
      exists: snap.exists(),
      data: snap.data()
    });

    if (!snap.exists()) {
      console.log("[SUB_STATES] no data for dayKey:", dayKey, "profile:", visibleProfile);
      callback({ completedDogs: {}, urgentDogs: {} });
      return;
    }
    const data = snap.data() || {};
    const completedDogs = data.completedDogs || {};
    const urgentDogs = data.urgentDogs || {};
    console.log("[SUB_STATES] calling callback with states", { 
      dayKey, 
      profile: visibleProfile,
      completedCount: Object.keys(completedDogs).length, 
      urgentCount: Object.keys(urgentDogs).length 
    });
    callback({ completedDogs, urgentDogs });
  });
}

/**
 * Guardado de routeStatus POR DÍA
 * Se guarda en routeStatusByDay[dayKey] en el documento del workspace
 * routeStatusForDay es un objeto: { profileName: "go" | "salimos" | "volvemos" | "fin" | null, ... }
 * 
 * Para ADMIN: guarda en raíz y en subcolección (para compatibilidad)
 * Para WORKER: guarda únicamente en subcolección (no tiene acceso a raíz)
 */
export async function saveRouteStatus(workspaceId, dayKey, routeStatusForDay = {}, userRole = null) {
  if (!ensureUid(workspaceId)) { 
    console.warn('[AUTH_NULL]', { fn:'saveRouteStatus' }); 
    return; 
  }

  if (!dayKey) {
    console.warn('[SAVE_ROUTE_STATUS] Missing dayKey');
    return;
  }

  console.log("[ROUTE_STATUS] save for dayKey:", dayKey, "status:", routeStatusForDay, "userRole:", userRole);

  try {
    // ADMIN: escribir en raíz (compatibilidad con app antigua) — best-effort.
    // Un fallo aquí (p.ej. Admin secundario sin permiso en root) no debe impedir
    // la escritura canónica en la subcolección (routeStatusByDay) de abajo.
    if (userRole !== 'worker') {
      const rootRef = doc(db, 'workspaces', workspaceId);
      const dataToSave = {
        routeStatusByDay: {
          [dayKey]: routeStatusForDay || {},
        },
        _routeStatusUpdatedAt: serverTimestamp()
      };

      try {
        console.log("[SAVE_ROUTE_STATUS] saving to root structure:", dataToSave);
        await setDoc(rootRef, dataToSave, { merge: true });
        console.log("[SAVE_ROUTE_STATUS] root ok");
      } catch (e) {
        console.warn("[SAVE_ROUTE_STATUS] root legacy write skipped", e?.code || e?.message);
      }
    }

    // ADMIN + WORKER: escribir en subcolección (autorizado a workers y admin secundario)
    const subcollRef = doc(db, 'workspaces', workspaceId, 'routeStatusByDay', dayKey);
    console.log("[SAVE_ROUTE_STATUS] saving to subcollection:", routeStatusForDay);
    await setDoc(subcollRef, routeStatusForDay || {}, { merge: true });
    console.log("[SAVE_ROUTE_STATUS] subcoll ok");
  } catch (e) {
    console.error("[SAVE_ROUTE_STATUS] FAIL", e.code || e.name, e.message);
    throw e;
  }

}

/**
 * Suscripción a cambios de routeStatus POR DÍA
 * Ambos ADMIN y WORKER escuchan la subcolección autorizada:
 * workspaces/{id}/routeStatusByDay/{dayKey}
 * 
 * Escritura:
 * - ADMIN: raíz (compatibilidad) + subdocumento (actual)
 * - WORKER: solo subdocumento (autorizado)
 * 
 * Lectura:
 * - ADMIN + WORKER: mismo subdocumento (bidireccional)
 * 
 * Devuelve el estado de ese día: { David: "go", Sara: "salimos", ... }
 */
export function subscribeRouteStatus(workspaceId, dayKey, callback, options = {}) {
  const { includeMetadataChanges = true, userRole = null } = options;
  if (!ensureUid(workspaceId)) { 
    console.warn('[AUTH_NULL]', { fn:'subscribeRouteStatus' }); 
    return () => {}; 
  }

  if (!dayKey) {
    console.warn('[SUB_ROUTE_STATUS] Missing dayKey, skipping subscription');
    return () => {};
  }

  console.log("[ROUTE_STATUS] subscribe for dayKey:", dayKey, "userRole:", userRole);

  // ✅ BIDIRECCIONAL: Admin y Worker escuchan el MISMO subdocumento
  const subcollRef = doc(db, 'workspaces', workspaceId, 'routeStatusByDay', dayKey);
  return onSnapshot(subcollRef, { includeMetadataChanges }, (snap) => {
    if (!snap.exists()) {
      console.log("[ROUTE_STATUS] no data for dayKey:", dayKey, "role:", userRole || 'admin');
      callback({});
      return;
    }
    const statusForDay = snap.data() || {};
    console.log("[ROUTE_STATUS] subscribe for dayKey:", dayKey, "role:", userRole || 'admin', "status:", statusForDay);
    callback(statusForDay);
   }, (error) => {
     // [RT_SUB_ERROR] Instrumentación: error callback de onSnapshot
     console.error("[RT_SUB_ERROR]", {
       workspace: workspaceId,
       dayKey,
       userRole: userRole || 'admin',
       code: error?.code,
       message: error?.message,
       timestamp: new Date().toISOString()
     });
   });
}

/**
 * Debounce para saveRouteStatus
 */
export const debouncedSaveRouteStatus = debounce(saveRouteStatus, 500, { 
  leading: false,
  trailing: true
});
